import React, { useState, useEffect } from "react";
import { X, Clock, TrendingUp, Users, Flame, Snowflake, MessageCircle } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const DOW_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const TEMP_COLORS = { hot: '#ef4444', warm: '#f59e0b', cold: '#3b82f6', frozen: '#94a3b8' };
const TEMP_LABELS = { hot: 'Quente', warm: 'Morno', cold: 'Frio', frozen: 'Inativo' };

const GroupInsights = ({ groupId, onClose }) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    api.get(`/grupos-whatsapp/${groupId}/insights`, { params: { cliente_id: user.cliente_id, days } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [groupId, days, user.cliente_id]);

  if (!groupId) return null;

  const bg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const border = darkMode ? 'border-zinc-700' : 'border-gray-200';
  const text = darkMode ? 'text-gray-200' : 'text-gray-800';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';

  // Build hour×dow grid
  const buildHeatmapGrid = (activity) => {
    const grid = Array.from({ length: 24 }, () => Array(7).fill(0));
    let max = 1;
    activity.forEach(a => { grid[a.hour][a.dow] = a.count; if (a.count > max) max = a.count; });
    return { grid, max };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className={`${bg} rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${border}`}>
          <div>
            <h2 className={`text-lg font-bold ${text}`}>{data?.group?.nome || 'Insights'}</h2>
            <p className={textMuted}>{data?.group?.total_participantes || 0} participantes</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={e => setDays(+e.target.value)}
              className={`text-sm rounded px-2 py-1 border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300'}`}>
              <option value={7}>7 dias</option>
              <option value={14}>14 dias</option>
              <option value={30}>30 dias</option>
              <option value={90}>90 dias</option>
            </select>
            <button onClick={onClose} className={`p-1 rounded hover:bg-opacity-20 hover:bg-gray-500`}><X className="h-5 w-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center"><div className="animate-spin h-8 w-8 border-2 border-nomoz-orange border-t-transparent rounded-full mx-auto" /></div>
        ) : !data ? (
          <div className={`p-8 text-center ${textMuted}`}>Sem dados disponíveis</div>
        ) : (
          <div className="p-4 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: MessageCircle, label: 'Mensagens', value: data.summary.total_messages, sub: `${data.summary.msgs_per_day}/dia` },
                { icon: Users, label: 'Ativos', value: data.summary.active_participants, sub: `de ${data.group.total_participantes}` },
                { icon: Snowflake, label: 'Lurkers', value: `${data.summary.lurker_pct}%`, sub: `${data.summary.lurkers} silenciosos` },
                { icon: Clock, label: 'Pico', value: data.summary.peak_hour ? `${data.summary.peak_hour.hour}h` : '-', sub: data.summary.peak_day ? data.summary.peak_day.day : '' },
              ].map((c, i) => (
                <div key={i} className={`p-3 rounded-lg border ${border} ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <c.icon className="h-4 w-4 text-nomoz-orange" />
                    <span className={`text-xs ${textMuted}`}>{c.label}</span>
                  </div>
                  <div className={`text-xl font-bold ${text}`}>{c.value}</div>
                  <div className={`text-xs ${textMuted}`}>{c.sub}</div>
                </div>
              ))}
            </div>

            {/* Activity Heatmap (hour × day of week) */}
            {data.activity_heatmap.length > 0 && (() => {
              const { grid, max } = buildHeatmapGrid(data.activity_heatmap);
              return (
                <div>
                  <h3 className={`text-sm font-semibold mb-2 ${text}`}>📊 Atividade por Horário</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className={`p-1 ${textMuted}`}></th>
                          {DOW_NAMES.map(d => <th key={d} className={`p-1 text-center ${textMuted}`}>{d}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {[6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,0,1].map(h => (
                          <tr key={h}>
                            <td className={`p-1 text-right pr-2 ${textMuted}`}>{h}h</td>
                            {Array.from({ length: 7 }, (_, d) => {
                              const v = grid[h][d];
                              const intensity = v / max;
                              const bg = v === 0 ? (darkMode ? 'rgba(63,63,70,0.5)' : 'rgba(243,244,246,1)')
                                : `rgba(249,115,22,${0.15 + intensity * 0.85})`;
                              return (
                                <td key={d} className="p-0.5">
                                  <div className="rounded" style={{ background: bg, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    title={`${DOW_NAMES[d]} ${h}h: ${v} msgs`}>
                                    {v > 0 && <span className={intensity > 0.5 ? 'text-white' : text} style={{ fontSize: 9 }}>{v}</span>}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* Daily Trend */}
            {data.daily_trend.length > 1 && (() => {
              const maxMsgs = Math.max(...data.daily_trend.map(d => d.messages), 1);
              return (
                <div>
                  <h3 className={`text-sm font-semibold mb-2 ${text}`}>📈 Tendência Diária</h3>
                  <div className="flex items-end gap-1" style={{ height: 100 }}>
                    {data.daily_trend.map((d, i) => {
                      const h = (d.messages / maxMsgs) * 100;
                      const dateStr = new Date(d.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center" title={`${dateStr}: ${d.messages} msgs, ${d.participants} participantes`}>
                          <div className="w-full rounded-t" style={{ height: `${h}%`, minHeight: d.messages > 0 ? 4 : 0, background: `rgba(249,115,22,${0.4 + (h/100)*0.6})` }} />
                          {data.daily_trend.length <= 14 && <span className={`text-[8px] mt-1 ${textMuted}`}>{dateStr}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Top Participants */}
            {data.top_participants.length > 0 && (
              <div>
                <h3 className={`text-sm font-semibold mb-2 ${text}`}>🏆 Top Participantes</h3>
                <div className="space-y-1">
                  {data.top_participants.map((p, i) => {
                    const maxMsgs = data.top_participants[0].messages;
                    const pct = (p.messages / maxMsgs) * 100;
                    return (
                      <div key={p.phone} className={`flex items-center gap-2 p-2 rounded ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'}`}>
                        <span className={`text-xs w-5 text-right font-bold ${i < 3 ? 'text-nomoz-orange' : textMuted}`}>{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${text}`}>{p.name || p.phone}</span>
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: TEMP_COLORS[p.temperature] }}
                              title={TEMP_LABELS[p.temperature]} />
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className={`h-1.5 rounded-full flex-1 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                              <div className="h-full rounded-full bg-nomoz-orange" style={{ width: `${pct}%` }} />
                            </div>
                            <span className={`text-xs ${textMuted}`}>{p.messages} msgs</span>
                          </div>
                        </div>
                        <span className={`text-xs ${textMuted}`}>{p.days_since_last === 0 ? 'hoje' : `${p.days_since_last}d`}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupInsights;
